import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { endsWith, pick } from 'lodash';
import { getCurrencyPrecision, shouldCommafy } from '../../utility/k_currency';
import { IVendorContext } from './types';
import VendorAPIManager, {
    EnableEnum,
    ICurrencyFormat,
    IQrDetailsResponse,
    IRestaurant,
    IRestaurantConfigRedirectPatch,
    OrderModeEnum,
} from '../../repository/vendor';
import { getError } from '../../utility/k_error';
import { Error } from '../../repository/common';
import { onPushDimensionEvent, onPushEvent } from '../../lib/gtm';
import InvoiceError from '../../component/InvoiceError';
import { getQRPath, IURLTopology, QSRQrModeEnum, QSRRouterModeEnum, useQlubRouter } from '../../hook/router';
import Routes from '../../service/routes';
import { snackBar } from '../../hook/snackBar';
import { getDiningSession } from '../../service/session/dsi';
import { kTrimEnd } from '../../utility/k_string';
import { applyThemeToURL } from '../../utility/theme';
import { qlubSessionStorage } from '../../service/storage/sessionStorage';
import { addParamsToURL } from '../../utility/k_url';
import { getDefaultLanguage } from '../../utility/k_lang';
import { useScreenTimeout } from './hook/screenTimeout';
import { shouldHideLogo, themeOptions } from '../config';
import { useAuth } from '../auth';

const getQsrSingleMenuKey = (url: IURLTopology) => {
    return `qlub.qsr.single_menu.${url.cc}@${url.slug}`;
};

const VendorContext = createContext<IVendorContext>({
    vendor: undefined,
    currencyFormat: {
        cs: '',
        cc: '',
        currencyPrecision: 2,
        currencyCommaSeparator: EnableEnum.Disable,
    },
    error: undefined,
    loading: false,
    tableName: '',
    setTableName: () => console.log('not implemented'),
    tableInfo: undefined,
    qsrSingleMenuId: '',
    qsrLoading: false,
    hideLogo: false,
});

interface IProps {
    vendor?: IRestaurant;
    getMenu?: (url: IURLTopology) => Promise<string>;
    children?: ReactNode;
}

export function VendorContextProvider({ vendor: ven, getMenu, children }: IProps) {
    const { url, urlStr, lang, push, pathname, source, routerPush, query, qrMode } = useQlubRouter();
    const { triggerSnackBar: enqueueSnackbar } = snackBar();
    const [vendor, setVendor] = useState<IRestaurant | undefined>(ven);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [tableName, setTableName] = useState<string>('');
    const [tableInfo, setTableInfo] = useState<IQrDetailsResponse | undefined>(undefined);
    const [qsrSingleMenuId, setQsrSingleMenuId] = useState<string>('');
    const qsrSingleMenuIdRef = useRef<string>('');
    const [qsrLoading, setQsrLoading] = useState(false);
    const { screenTimeoutContent } = useScreenTimeout(vendor);
    const { setLoginVendorLogo } = useAuth();

    const dimensionRef = useRef<boolean>(false);

    useEffect(() => {
        if (!url.cc || !vendor?.posVendor || dimensionRef.current) {
            return;
        }

        dimensionRef.current = true;
        onPushDimensionEvent({
            cc: url.cc,
            pos_id: vendor?.posVendor?.posId,
        });
    }, [url.cc, vendor?.posVendor?.posId]);

    const hideLogo = useMemo(() => {
        return shouldHideLogo(query.hideLogo, vendor?.config?.hideLogo);
    }, [vendor]);

    const getMenuId = () => {
        return getMenu?.(url)
            .then((menuId) => {
                if (url && menuId) {
                    qlubSessionStorage.set(getQsrSingleMenuKey(url), menuId);
                }
                qsrSingleMenuIdRef.current = menuId;
                setQsrSingleMenuId(menuId);
                return menuId;
            })
            .finally(() => {
                setQsrLoading(false);
            });
    };

    const menuRouter = (routerLang: any, menuId?: string) => {
        if (menuId) {
            return routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr/[menu]', {
                menu: menuId,
                qsr: qrMode === QSRQrModeEnum.MenuOnly ? QSRRouterModeEnum.DigitalMenu : QSRRouterModeEnum.True,
                ...routerLang,
            });
        }
        return routerPush(`/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr`, {
            qsr: qrMode === QSRQrModeEnum.MenuOnly ? QSRRouterModeEnum.DigitalMenu : QSRRouterModeEnum.True,
            ...routerLang,
        });
    };

    const gotoQsr = (routerLang: any, menuId?: string) => {
        if (menuId) {
            menuRouter(routerLang, menuId);
        } else {
            setQsrLoading(true);
            getMenuId()?.then((mId) => {
                menuRouter(routerLang, mId);
            });
        }
    };

    const checkQSRMenu = (res: IRestaurant) => {
        if (
            ![OrderModeEnum.QSR, OrderModeEnum.DigitalMenu, OrderModeEnum.Mixed].includes(
                (res.orderMode || OrderModeEnum.Default) as any,
            )
        ) {
            return;
        }

        if (qsrSingleMenuIdRef.current) {
            return;
        }

        getMenuId();
    };

    const applyVendorData = (res: IRestaurant) => {
        const currentUrl = `/qr/${getQRPath(url)}`;
        const redirectUrl = res.redirect || res.metaObject?.redirectionUrl;

        let routerTheme: any = null;
        if (res?.config?.themeMode && !themeOptions.inUrl) {
            applyThemeToURL(res.config.themeMode);
            routerTheme = {
                theme: res.config.themeMode,
            };
        }
        const root = document.documentElement;
        root.style.setProperty('--vendor-primary-color', res.primaryColour);
        root.style.setProperty('--vendor-secondary-color', res.secondaryColour);

        if (
            redirectUrl &&
            currentUrl !== redirectUrl &&
            (redirectUrl.startsWith('https') ||
                (!redirectUrl.startsWith('https') && Routes.getInstance().exists(redirectUrl))) &&
            (!res.config.redirectPreventCircular || (res.config.redirectPreventCircular && url.redirected !== 'true'))
        ) {
            if (redirectUrl.startsWith('https')) {
                if (res.config.redirectPreventCircular) {
                    window.location.assign(`${redirectUrl}?redirected=true`);
                    return Promise.resolve(false);
                }
                window.location.assign(redirectUrl);
                return Promise.resolve(false);
            }
            const isSameTable = redirectUrl.includes(currentUrl) || currentUrl.includes(redirectUrl);
            if (res.config.redirectPreventCircular) {
                routerPush(redirectUrl, {
                    redirected: true,
                });
                return Promise.resolve(isSameTable);
            }
            push(addParamsToURL(redirectUrl, pick(query, ['lang', 'theme', 'redirected', 'loyalty_cuid'])));
            return Promise.resolve(isSameTable);
        }
        if (pathname === '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]') {
            const redirectPatch = res?.config?.redirectPatch;
            if (
                redirectPatch === IRestaurantConfigRedirectPatch.IdAfterHash &&
                query.patched !== 'true' &&
                endsWith(url.hash, url.id)
            ) {
                routerPush(`/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]`, {
                    hash: kTrimEnd(url.hash, url.id),
                    patched: 'true',
                }).then(() => {
                    location.reload();
                });
                return Promise.resolve(false);
            }
        }

        const forceApply = ['redirect'].includes(query?.init);
        const targetPathname = forceApply ? pathname : '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]';
        // redirect for qsr
        if (pathname === targetPathname) {
            onPushEvent('scan_qr', {
                table_param: `${url.id}/${url.f1}/${url.f2}`,
                country_code: url.cc,
                applied_discount: 'disable',
                opsMode: res.opsMode,
                restaurant_name: url.slug,
                source,
                diningSessionId: getDiningSession(url)?.id,
                bolVariant: vendor?.config?.bolVariant ?? vendor?.country?.config?.bolVariant,
            });

            const defaultLang = getDefaultLanguage(res?.config?.defaultLang);
            const routerLang =
                defaultLang && defaultLang !== lang
                    ? {
                          lang: defaultLang,
                          ...routerTheme,
                      }
                    : routerTheme;

            if (!res.orderConfig?.manualAmount) {
                if (res.orderMode === OrderModeEnum.QSR || qrMode === QSRQrModeEnum.MenuOnly) {
                    gotoQsr(routerLang, qsrSingleMenuIdRef.current);
                    return Promise.resolve(true);
                }
            }
            if (defaultLang) {
                routerPush(targetPathname, {
                    ...routerLang,
                });
            } else if (routerTheme && query.theme !== routerTheme.theme) {
                routerPush(targetPathname, {
                    ...routerTheme,
                    ...routerLang,
                });
            }
        }
        return Promise.resolve(true);
    };

    const getVendorData = () => {
        setLoading(true);
        VendorAPIManager.getInstance()
            .getRestaurantInfo(url)
            .then((res) => {
                return applyVendorData(res).then((ok) => {
                    if (ok) {
                        checkQSRMenu(res);
                        setVendor(res);
                        setLoading(false);
                    }
                });
            })
            .catch((err: Error) => {
                enqueueSnackbar(getError(err), { variant: 'error' });
                setError(err);
                setLoading(false);
            });
    };

    const getTableInfo = () => {
        if (tableInfo && tableInfo.table.id === url.id) {
            return;
        }

        if (!url.id || url.id === '_') {
            setTableInfo({
                table: {
                    id: url.id,
                    preferredName: '',
                    revenueCenter: '',
                    section: '',
                    tableName: '',
                },
            });
            return;
        }

        VendorAPIManager.getInstance()
            .getQrDetails({
                cc: url.cc,
                restaurantUnique: url.slug,
                tableID: url.id,
                f1: url.f1,
                f2: url.f2,
                hash: url.hash,
            })
            .then((res) => setTableInfo(res))
            .catch(() => {
                setTableInfo({
                    table: {
                        id: url.id,
                        preferredName: '',
                        revenueCenter: '',
                        section: '',
                        tableName: '',
                    },
                });
            });
    };

    useEffect(() => {
        if (!url.cc || !url.slug) {
            return;
        }

        qsrSingleMenuIdRef.current = qlubSessionStorage.get(getQsrSingleMenuKey(url));
        setQsrSingleMenuId(qsrSingleMenuIdRef.current);
        getTableInfo();
        getVendorData();
    }, [urlStr]);

    useEffect(() => {
        setLoginVendorLogo?.(vendor?.logoSmallUrl ?? null);
        const html = document.getElementsByTagName('html')[0];
        if (html && vendor) {
            html.setAttribute('loaded', 'true');
        }
    }, [vendor]);

    const currencyFormat = useMemo<ICurrencyFormat>(() => {
        const v = vendor || ven;
        const commaSeparate = shouldCommafy(v?.country?.currencyCode);
        return {
            cs: v?.country?.currencySymbol || '',
            cc: v?.country?.currencyCode || '',
            currencyPrecision: getCurrencyPrecision(v?.country?.currencyCode || ''),
            currencyCommaSeparator: commaSeparate
                ? EnableEnum.Enable
                : v?.config?.currencyCommaSeparator || EnableEnum.Disable,
        };
    }, [vendor, ven]);

    const retryHandler = () => {
        getVendorData();
    };

    return (
        <VendorContext.Provider
            value={{
                error,
                vendor,
                currencyFormat,
                loading,
                tableName,
                setTableName,
                tableInfo,
                qsrSingleMenuId,
                qsrLoading,
                hideLogo,
            }}
        >
            {screenTimeoutContent}
            {error ? (
                <InvoiceError url={url} err={error} vendor={vendor} lang={lang} onRetry={retryHandler} />
            ) : (
                children
            )}
        </VendorContext.Provider>
    );
}

export const useVendor = () => useContext(VendorContext);
